(() => {
  // Cross-document page transitions runtime (Tier 1).
  // The CSS opt-in (@view-transition { navigation: auto }) lives inline in
  // layout/theme.liquid so it is present before either document's first paint;
  // this script only guards and polishes the transition:
  //   - opt out in known in-app WebViews (they freeze/white-screen on
  //     cross-document transitions, June 2026 testing);
  //   - skip on low-power devices and reduced motion;
  //   - cancel on user interaction to protect INP (see Shopify's
  //     cancel-view-transitions-on-interaction best practice);
  //   - tag every transition with the page-navigation type for future CSS.
  // Mirrors the guard logic in Horizon's assets/view-transitions.js.

  function prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function isLowPowerDevice() {
    return (
      Number(navigator.hardwareConcurrency) <= 2 ||
      Number(navigator.deviceMemory) <= 2
    );
  }

  function shouldDisableCrossDocumentViewTransitions(userAgent = navigator.userAgent) {
    const ua = userAgent || '';
    const androidWebView = /\bAndroid\b/i.test(ua) && /;\s?wv\)/i.test(ua);
    const knownInAppBrowser =
      /\b(FBAN|FBAV|FB_IAB|FBIOS|Instagram|musical_ly|Bytedance|BytedanceWebview|trill|TikTok)(?:\b|_)/i.test(
        ua
      );
    return androidWebView || knownInAppBrowser;
  }

  function isViewTransition(value) {
    return typeof ViewTransition !== 'undefined' && value instanceof ViewTransition;
  }

  if (shouldDisableCrossDocumentViewTransitions()) {
    // A later @view-transition rule wins the cascade, so `navigation: none`
    // defeats the opt-in emitted by layout/theme.liquid.
    const style = document.createElement('style');
    style.textContent = '@view-transition { navigation: none; }';
    (document.head || document.documentElement).appendChild(style);
  }

  function shouldSkip(viewTransition) {
    return (
      !isViewTransition(viewTransition) ||
      isLowPowerDevice() ||
      prefersReducedMotion() ||
      shouldDisableCrossDocumentViewTransitions()
    );
  }

  function cancelOnInteraction(viewTransition) {
    const cancel = () => viewTransition.skipTransition();
    ['pointerdown', 'keydown'].forEach((eventName) => {
      document.addEventListener(eventName, cancel, { once: true });
    });
  }

  function setDefaultType(viewTransition) {
    if (viewTransition.types) {
      viewTransition.types.clear();
      viewTransition.types.add('page-navigation');
    }
  }

  // Outgoing document: covers the browser's blocked-rendering period.
  window.addEventListener('pageswap', (event) => {
    const { viewTransition } = event;
    if (!viewTransition) {
      return;
    }
    if (shouldSkip(viewTransition)) {
      viewTransition.skipTransition();
      return;
    }
    cancelOnInteraction(viewTransition);
    setDefaultType(viewTransition);
  });

  // Incoming document: covers the transition animation itself.
  window.addEventListener('pagereveal', (event) => {
    const { viewTransition } = event;
    if (!viewTransition) {
      return;
    }
    if (shouldSkip(viewTransition)) {
      viewTransition.skipTransition();
      return;
    }
    cancelOnInteraction(viewTransition);
    setDefaultType(viewTransition);
  });
})();